import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { QueryParamsModel, QueryResultsModel } from "../../_base/crud";
import { Permission } from "../_models/permission.model";
import { Role } from "../_models/role.model";
import { User } from "../_models/user.model";
import { UserValidation } from "../_models/userValidation.model";
import {HTTPService} from "../../../service/http.service";
import {DuplicateUserParam} from "../../../persistence/dto/duplicate-user-param.model";
import {HTTPRequestBuilder} from "../../../model/http.request.model";
import {HTTPMethod} from "../../../model/http-method.enum";
const API_LOGIN_URL = environment.API_URL + "api/account/login";
const API_FINDUSERS_URL = environment.API_URL + "api/users/find";
const API_CRM_FINDUSERS_URL = environment.API_URL + "api/users/findcrm";

const API_USERS_URL = environment.API_URL + "api/users";
const API_PERMISSION_URL = environment.API_URL + "api/permissions";
const API_ROLES_URL = environment.API_URL + "api/roles";
const API_FORGOT = environment.API_URL + "api/account/forgot";

@Injectable()
export class AuthService {
  constructor(private http: HttpClient, private httpService: HTTPService) {}

  // Authentication/Authorization
  login(username: string, password: string): Observable<any> {
    return this.http.post<any>(API_LOGIN_URL, { username, password });
  }

  getTokenExpirationDate(token: string): Date {
    const helper = new JwtHelperService();
    const decoded = helper.decodeToken(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  getToken(): string {
    return localStorage.getItem(environment.authTokenKey);
  }

  isTokenExpired(token?: string): boolean {
    if (!token) {
      token = this.getToken();
    }
    if (!token) {
      return true;
    }

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) {
      return false;
    }
    return !(date.valueOf() > new Date().valueOf());
  }

  getUserByToken(): Observable<User> {
    const userToken = localStorage.getItem(environment.authTokenKey);
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(userToken);
    return new Observable<User>((subscriber) => {
      const user = new User();
      user.firstName = decodedToken.nam ? decodedToken.nam : "";
      user.roles = [decodedToken.roles];      // BUG!: JwtHelperService no maneja bien el array y requiere flattenización.
      user.id = decodedToken.uid;
      subscriber.next(user);
    });
  }

  register(user: User): Observable<any> {
    const httpHeaders = new HttpHeaders();
    httpHeaders.set("Content-Type", "application/json");
    return this.http
      .post<User>(API_USERS_URL, user, { headers: httpHeaders })
      .pipe(
        map((res: User) => {
          return res;
        }),
        catchError((err) => {
          return null;
        })
      );
  }

  public requestPassword(data): Observable<any> {
    data = { userName: data };
    return this.http.post(API_FORGOT, data, { observe: "response" });
  }

  getAllUsers(): Observable<User[]> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<User[]>(API_USERS_URL, { headers: httpHeaders });
  }

  getUserById(userId: string): Observable<User> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<User>(API_USERS_URL + `/${userId}`, {
      headers: httpHeaders
    });
  }

  blockUser(userId: string): Observable<User> {
    const url = `${API_USERS_URL}/${userId}/block`;

    return this.http.patch<User>(url, {}).pipe(catchError(this.handleError));
  }

  activateuser(userId: string): Observable<User> {
    const url = `${API_USERS_URL}/${userId}/activate`;

    return this.http.patch<User>(url, {}).pipe(catchError(this.handleError));
  }

  // UPDATE => PUT: update the user on the server
  updateUser(_user: User): Observable<any> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.put(API_USERS_URL + `/${_user.id}`, _user, {
      headers: httpHeaders
    });
  }

  // CREATE =>  POST: add a new user to the server
  createUser(user: User): Observable<User> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http
      .post<User>(API_USERS_URL, user, { headers: httpHeaders })
      .pipe(
        tap((data) => console.log("createProduct: " + JSON.stringify(data))),
        catchError(this.handleError)
      );
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  findUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    return this.http.post<QueryResultsModel>(API_FINDUSERS_URL, queryParams);
  }

  findCRMUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    return this.http.post<QueryResultsModel>(API_CRM_FINDUSERS_URL, queryParams);
  }

  // Permission
  getAllPermissions(): Observable<Permission[]> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<Permission[]>(API_PERMISSION_URL, {
      headers: httpHeaders
    });
  }

  getRolePermissions(roleId: number): Observable<Permission[]> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<Permission[]>(API_PERMISSION_URL + "/getRolePermission?=" + roleId, { headers: httpHeaders });
  }

  // Roles
  getAllRoles(): Observable<Role[]> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<Role[]>(API_ROLES_URL, { headers: httpHeaders });
  }

  getRoleById(roleId: string): Observable<Role> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<Role>(API_ROLES_URL + `/${roleId}`, {
      headers: httpHeaders
    });
  }

  // CREATE =>  POST: add a new role to the server
  createRole(role: Role): Observable<Role> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.post<Role>(API_ROLES_URL, role, {
      headers: httpHeaders
    });
  }

  // UPDATE => PUT: update the role on the server
  updateRole(role: Role): Observable<any> {
    const httpHeaders = this.GetAuthTokenHeader();
    const url = `${API_ROLES_URL}/${role.id}`;
    return this.http.put(url, role, { headers: httpHeaders });
  }

  // DELETE => delete the role from the server
  deleteRole(roleId: string): Observable<Role> {
    const httpHeaders = this.GetAuthTokenHeader();
    const url = `${API_ROLES_URL}/${roleId}`;
    return this.http.delete<Role>(url, { headers: httpHeaders });
  }

  // Check Role Before deletion
  isRoleAssignedToUsers(roleId: number): Observable<boolean> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.get<boolean>(API_ROLES_URL + "/checkIsRollAssignedToUser?roleId=" + roleId, {
      headers: httpHeaders
    });
  }

  findRoles(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const httpHeaders = this.GetAuthTokenHeader();
    return this.http.post<QueryResultsModel>(API_ROLES_URL + "/findRoles", queryParams, { headers: httpHeaders });
  }

  public validateDuplicateValues(objectValidation : UserValidation) : Observable<DuplicateUserParam>{
      return this.httpService.send<UserValidation, DuplicateUserParam>(
          new HTTPRequestBuilder<UserValidation>()
              .method(HTTPMethod.POST)
              .controllerPath('api/Users')
              .relativePath('ValidateUserData')
              .payload(objectValidation)
              .build()
      ).pipe(map(duplicateUserParam => DuplicateUserParam.from(duplicateUserParam)));
  }

  private GetAuthTokenHeader() {
    const userToken = localStorage.getItem(environment.authTokenKey);
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append("Authorization", "Bearer " + userToken);
    httpHeaders.set("Content-Type", "application/json");
    return httpHeaders;
  }

  private handleError(err) {
    const errorMessage: string[] = [];
    if (err.error instanceof ErrorEvent) {
      // For client-side errors
      errorMessage.push(`An error occurred: ${err.error.message}`);
    } else {
      // For backend error codes (4xx-5xx)
      if (err.status === 422) {
        // handle validation error
        const validationErrorDictionary = err.error;
        for (const fieldName in validationErrorDictionary) {
          if (validationErrorDictionary.hasOwnProperty(fieldName)) {
            validationErrorDictionary[fieldName].forEach((elem) => errorMessage.push(elem));
          }
        }
      } else if (err.error[0]) {
        errorMessage.push(`${err.error[0].description}`);
      } else {
        errorMessage.push(`Backend returned code ${err.status}: ${err.error}`);
      }
    }
    return throwError(errorMessage);
  }
}
